/* Place & Visit: the open state, the day bar, the map, and the hero drift.
 *
 * Entrance motion is not here. theme.js owns that for the whole site and
 * already tags these sections; a second observer would fight it.
 *
 * Everything in this file degrades to nothing. With no script the state line
 * still shows the branch's own hours as plain text, the day bar sits empty,
 * the map section simply never initialises, and the hero holds still.
 */
(function () {
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* A clock time as minutes past midnight. Returns null rather than guessing:
     a malformed setting should leave the state alone, not put the marker
     somewhere arbitrary. */
  function minutes(text) {
    var m = String(text || '').match(/(\d{1,2}):(\d{2})/);
    if (!m) return null;
    var h = parseInt(m[1], 10), mm = parseInt(m[2], 10);
    if (h > 23 || mm > 59) return null;
    return h * 60 + mm;
  }

  function states(root) {
    var nodes = (root || document).querySelectorAll('[data-pl-state]');
    Array.prototype.forEach.call(nodes, function (el) {
      var from = minutes(el.getAttribute('data-from'));
      var to = minutes(el.getAttribute('data-to'));
      if (from === null || to === null || to <= from) return;

      var now = new Date();
      var mins = now.getHours() * 60 + now.getMinutes();
      var open = mins >= from && mins < to;
      var pct = Math.max(0, Math.min(1, (mins - from) / (to - from)));

      var chip = el.querySelector('.pl-state');
      if (chip) {
        chip.classList.toggle('is-shut', !open);
        var txt = chip.querySelector('.txt');
        var label = el.getAttribute(open ? 'data-open-label' : 'data-shut-label');
        if (txt && label) txt.textContent = label;
      }
      var bar = el.querySelector('.pl-day');
      if (bar) {
        var fill = bar.querySelector('b');
        var mark = bar.querySelector('u');
        if (fill) fill.style.inlineSize = (pct * 100).toFixed(1) + '%';
        if (mark) mark.style.insetInlineStart = (pct * 100).toFixed(1) + '%';
        bar.classList.add('is-set');
      }
    });
  }

  function maps(root) {
    if (!window.L) return;
    var nodes = (root || document).querySelectorAll('[data-pl-map]');
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.getAttribute('data-pl-ready')) return;
      var raw = el.getAttribute('data-pl-map') || '';
      var pins = raw.split('~').map(function (p) {
        var bits = p.split('|');
        var lat = parseFloat(bits[1]);
        var lng = parseFloat(bits[2]);
        if (isNaN(lat) || isNaN(lng)) return null;
        return { name: bits[0], at: [lat, lng] };
      }).filter(Boolean);
      if (!pins.length) return;
      el.setAttribute('data-pl-ready', '1');

      var map = L.map(el, { scrollWheelZoom: false, attributionControl: true });
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(map);

      pins.forEach(function (p) {
        L.marker(p.at, {
          icon: L.divIcon({
            className: '',
            html: '<div class="pl-pin"></div><div class="pl-pin-l">' + p.name + '</div>',
            iconSize: [12, 12],
            iconAnchor: [6, 6]
          }),
          keyboard: false,
          alt: p.name
        }).addTo(map);
      });

      if (pins.length === 1) {
        map.setView(pins[0].at, parseInt(el.getAttribute('data-zoom'), 10) || 16);
      } else {
        map.fitBounds(pins.map(function (p) { return p.at; }), { padding: [70, 90] });
      }
    });
  }

  /* The wipe is this group's own, and theme.js has never heard of it.
     `.reveal-ready [data-pl-wipe] img` clips the photograph to nothing, and
     theme.js only ever reveals `[data-reveal]` — so without this the frames
     stay clipped for good and every gallery cell renders as empty paper. */
  function wipes(root) {
    var nodes = (root || document).querySelectorAll('[data-pl-wipe]');
    if (!nodes.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(nodes, function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var d = parseInt(e.target.getAttribute('data-delay') || '0', 10);
        window.setTimeout(function () { e.target.classList.add('is-revealed'); }, d);
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -6% 0px' });
    window.requestAnimationFrame(function () {
      Array.prototype.forEach.call(nodes, function (el) {
        /* Anything already on screen at first paint is shown outright, so a
           frame above the fold never waits on an observer. */
        if (el.getBoundingClientRect().top < window.innerHeight) {
          el.classList.add('is-revealed');
        } else {
          io.observe(el);
        }
      });
    });
  }

  /* The hero drifts against the scroll.

     Two things made it jump. It carried no transform until the first scroll
     event, so the first event snapped it from nothing to its computed offset
     in one frame; and the transform included `scale(1.07)`, so that snap
     changed the photograph's size as well as its position. It is positioned
     once on load now, and the overscan comes from the element's negative
     inset rather than a scale, so nothing ever resizes.

     The amplitude is a share of the frame's own height, which keeps the drift
     inside that inset at every viewport — a fixed 30px would have pulled a
     bare edge into view on a short hero. */
  var pars = [];

  function drift(p) {
    var r = p.parentNode.getBoundingClientRect();
    if (!r.height) return;
    var amp = r.height * 0.055;
    var t = (r.top + r.height / 2 - window.innerHeight / 2) / window.innerHeight;
    p.style.transform = 'translate3d(0,' + (t * -amp).toFixed(1) + 'px,0)';
  }

  function parallax() {
    if (reduce) return;
    pars = [].slice.call(document.querySelectorAll('.pl-par'));
    if (!pars.length) return;

    /* Positioned before a single scroll event, so there is no first-move jump. */
    pars.forEach(drift);

    var ticking = false;
    function pass() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        pars.forEach(function (p) {
          var r = p.parentNode.getBoundingClientRect();
          if (r.bottom < 0 || r.top > window.innerHeight) return;
          drift(p);
        });
        ticking = false;
      });
    }
    window.addEventListener('scroll', pass, { passive: true });
    window.addEventListener('resize', pass, { passive: true });
  }

  function start(root) {
    states(root);
    maps(root);
    wipes(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      start();
      parallax();
    });
  } else {
    start();
    parallax();
  }
  /* Leaflet is deferred, so its globals may only exist by load. */
  window.addEventListener('load', function () { maps(); });
  /* The editor re-renders a section on its own; re-run for that subtree. */
  document.addEventListener('shopify:section:load', function (e) { start(e.target); });
})();
